/* eslint-disable react/forbid-prop-types */
import React, {
   useCallback,
   useEffect,
   useMemo,
   useRef,
   useState,
} from 'react';
import PropTypes from 'prop-types';

import HFIcon from '../icons';
import { readableInk } from '../derivations';
import clearTextSelection from '../domSelection';

// ─── Footer da rota: km estimada + duração planejada (início/fim) ───────────
function formatKm(meters) {
   if (meters == null) return '—';
   return `${(Number(meters) / 1000).toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
   })} km`;
}

function formatRouteDuration(seconds) {
   if (seconds == null) return '—';
   const totalMin = Math.round(Number(seconds) / 60);
   const h = Math.floor(totalMin / 60);
   const m = totalMin % 60;
   if (h > 0) return `${h}h ${String(m).padStart(2, '0')}min`;
   return `${m}min`;
}

// Versão compacta p/ a linha de metadados do card colapsado (ex.: "1h20").
function compactRouteDuration(seconds) {
   if (seconds == null) return null;
   const totalMin = Math.round(Number(seconds) / 60);
   const h = Math.floor(totalMin / 60);
   const m = totalMin % 60;
   if (h > 0) return `${h}h${String(m).padStart(2, '0')}`;
   return `${m}min`;
}

function toHHMM(timeStr) {
   if (!timeStr) return null;
   const s = String(timeStr).slice(0, 5);
   return /^\d{2}:\d{2}$/.test(s) ? s : null;
}

function minutesToHHMM(min) {
   const norm = ((min % 1440) + 1440) % 1440;
   return `${String(Math.floor(norm / 60)).padStart(2, '0')}:${String(
      norm % 60
   ).padStart(2, '0')}`;
}

// Máquina de estados da rota (handoff 0042 §2) — rótulo + classe do badge.
const ROUTE_STATE_META = {
   RASCUNHO: { label: 'Rascunho', cls: 'draft' },
   DESIGNADA: { label: 'Técnico designado', cls: 'designated' },
   ATRIBUIDA: { label: 'Rota atribuída', cls: 'assigned' },
};

// Mapeadores de classe (evitam ternários aninhados nos JSX/derivações).
function seqClassFromStatus(status) {
   if (status === 'done') return 'done';
   if (status === 'run') return 'run';
   return '';
}

function dropClassFromPosition(position) {
   if (position === 'before') return 'drop-before';
   if (position === 'after') return 'drop-after';
   return '';
}

function pillClassFromStatus(status) {
   if (status === 'run') return 'run';
   if (status === 'warn') return 'warn';
   return 'idle';
}

function progressSegClass(index, doneCount, isRun) {
   if (index < doneCount) return 'done';
   if (index === doneCount && isRun) return 'run';
   return '';
}

// Ícone de status do item — convive com o número de sequência (que mantém a
// correspondência com os pinos do mapa): check = realizado, X = não
// realizado, relógio = roteirizado aguardando.
function deriveStatusIcon(stop) {
   const itemStatus = stop.raw?.item_status;
   if (itemStatus === 'REALIZADO' || (!stop.raw && stop.status === 'done')) {
      const events = stop.raw?.active_events || [];
      const lastOutcome = [...events]
         .reverse()
         .find(
            (ev) =>
               ev.event_kind === 'EXECUTED' || ev.event_kind === 'PARTIAL'
         );
      if (lastOutcome?.event_kind === 'PARTIAL') {
         return {
            cls: 'partial',
            icon: HFIcon.check,
            title: 'Realizado parcial',
         };
      }
      return { cls: 'ok', icon: HFIcon.check, title: 'Realizado' };
   }
   if (itemStatus === 'NAO_REALIZADO') {
      return { cls: 'fail', icon: HFIcon.x, title: 'Não realizado' };
   }
   return { cls: 'wait', icon: HFIcon.clock, title: 'Aguardando' };
}

// Progresso derivado dinamicamente das paradas — sobrevive a drag-and-drop.
function deriveProgress(stops) {
   const arr = stops || [];
   return {
      total: arr.length,
      done: arr.filter((s) => s.status === 'done').length,
      hasRun: arr.some((s) => s.status === 'run'),
   };
}

function StopRow({
   stop,
   isLast,
   isSelected = false,
   onClick,
   subclusterId,
   index,
   isDragging = false,
   dropPosition = null,
   canDrag = true,
   routeColor = null,
   onDragStart,
   onDragEnd,
   onDragOver,
   onDragLeave,
   onDrop,
}) {
   const seqClass = seqClassFromStatus(stop.status);
   const statusIcon = deriveStatusIcon(stop);

   const [showEvents, setShowEvents] = useState(false);
   // Timeline completa do snapshot (active_events) — fonte do expander. O mock
   // (HF_*_STOPS) não passa pelo adapter, então cai no resumo presence/otw.
   const timeline = stop.timeline ?? null;
   const eventCount = timeline
      ? timeline.length
      : (stop.presence?.length || 0) + (stop.otw ? 1 : 0);

   // "Iniciado" = item na MESA DE OPERAÇÃO: tem evento (intermediário ou
   // terminal) OU é terminal/done (inclui legado sem timeline). Item idle puro
   // (sem nenhum evento) = NÃO iniciado → círculo branco com borda/nº na cor da
   // rota. Mesma lógica vale para o pino no mapa.
   const started = stop.status === 'done' || eventCount > 0;

   // Canto direito (empilhado): ETA + tempo total de atendimento. O tempo vem do
   // serviço atribuído (estimated_duration_minutes); sem serviço/tempo → "—".
   const etaLabel =
      stop.eta && stop.eta !== '—' ? `ETA ${stop.eta}` : '—';
   const svcLabel =
      stop.estimatedDuration != null && stop.estimatedDuration > 0
         ? `${stop.estimatedDuration}min`
         : '—';

   const isFailed = stop.raw?.item_status === 'NAO_REALIZADO';

   // Item travado (M2 §2): tem evento de status ativo → âncora fixa. Não
   // arrasta; o board só destrava ao remover o evento na timeline do drawer.
   const isLocked = Boolean(stop.locked);
   const canDragRow = canDrag && !isLocked;

   // Quebra de janela (handoff 0049): item roteirizado cuja ETA passou do fim do
   // horário marcado — só acontece por reorder manual. Ganha o selo "fora do
   // horário" na sub-linha. É aviso, não trava (não mexe no drag).
   const breached = Boolean(stop.timeWindowBreach);

   const rowRef = useRef(null);
   useEffect(() => {
      if (isSelected && rowRef.current) {
         rowRef.current.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth',
         });
      }
   }, [isSelected]);

   const handleKey = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
         e.preventDefault();
         onClick();
      }
   };

   const dropClass = dropClassFromPosition(dropPosition);

   const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // eslint-disable-next-line no-param-reassign
      e.dataTransfer.dropEffect = 'move';
      const rect = e.currentTarget.getBoundingClientRect();
      const before = e.clientY - rect.top < rect.height / 2;
      onDragOver({ subclusterId, index: before ? index : index + 1 });
   };

   const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      onDrop();
   };

   return (
      <>
         <div
            ref={rowRef}
            className={`hf-stop ${
               isSelected ? 'selected' : ''
            } ${dropClass} ${isDragging ? 'dragging' : ''} ${
               isFailed ? 'failed' : ''
            } ${isLocked ? 'locked' : ''}`}
            onClick={onClick}
            onKeyDown={handleKey}
            onMouseUp={clearTextSelection}
            role="button"
            tabIndex={0}
            onDragOver={handleDragOver}
            onDragLeave={onDragLeave}
            onDrop={handleDrop}
         >
            {/* Alça de arraste — única área que inicia o reordenamento/realocação
               (evita drag acidental ao clicar na parada). Azul no hover quando a
               parada pode ser arrastada. */}
            <button
               type="button"
               className="hf-drag-handle"
               aria-label="Arrastar parada"
               title={
                  canDragRow
                     ? 'Arraste para reordenar ou realocar'
                     : 'Parada travada — não pode ser arrastada'
               }
               draggable={canDragRow}
               disabled={!canDragRow}
               onClick={(e) => e.stopPropagation()}
               onDragStart={(e) => {
                  if (!canDragRow) return;
                  e.dataTransfer.setData('text/plain', stop.id);
                  e.dataTransfer.effectAllowed = 'move';
                  // Preview = parada inteira (não só a alça).
                  const row = e.currentTarget.closest('.hf-stop');
                  if (row) e.dataTransfer.setDragImage(row, 24, 16);
                  onDragStart({
                     source: 'route',
                     id: stop.id,
                     item: stop,
                     subclusterId,
                     index,
                  });
               }}
               onDragEnd={onDragEnd}
            >
               {HFIcon.grip}
            </button>
            <div
               className={`seq ${seqClass} ${started ? 'started' : 'pristine'}`}
               style={
                  // eslint-disable-next-line no-nested-ternary
                  routeColor
                     ? started
                        ? {
                             background: routeColor,
                             borderColor: routeColor,
                             color: readableInk(routeColor),
                          }
                        : {
                             background: '#fff',
                             borderColor: routeColor,
                             borderWidth: '2px',
                             color: routeColor,
                          }
                     : undefined
               }
            >
               {stop.seq}
            </div>
            <div
               className={`status-ico ${statusIcon.cls}`}
               title={statusIcon.title}
            >
               {statusIcon.icon}
            </div>
            <div className="body">
               {/* Linha 1: nº do pedido em destaque (PED-…). Linha 2: nome do
                  cliente (trunca com "…" antes da coluna direita). */}
               <div className="ord">PED-{stop.id}</div>
               <div className="nm">{stop.nm}</div>
            </div>
            {/* Coluna direita: classe de carga no topo (alinhada ao PED), depois
               ETA e o tempo de atendimento. */}
            <div className="right">
               <span
                  className={`cargo-badge ${
                     stop.cargoClass ? `c-${stop.cargoClass}` : 'na'
                  }`}
                  title={
                     stop.cargoClass
                        ? `Classe de carga: ${stop.cargoClass}`
                        : 'Classe de carga não definida.'
                  }
               >
                  {stop.cargoClass || 'N/A'}
               </span>
               <div className="time tabular">{etaLabel}</div>
               <div className="dur tabular">{svcLabel}</div>
            </div>
            {/* Sub-linha DENTRO do card do item: toggle de eventos (alinhado ao
               balão de sequência) à esquerda + TRAVADO/LEGADO à direita. */}
            {(eventCount > 0 || isLocked || stop.isLegacyDone || breached) && (
               <div className="hf-stop-subline">
                  {eventCount > 0 ? (
                     <button
                        type="button"
                        className="hf-ev-toggle"
                        onClick={(e) => {
                           e.stopPropagation();
                           setShowEvents((prev) => !prev);
                        }}
                     >
                        {showEvents ? HFIcon.chevU : HFIcon.chevD}
                        {eventCount} {eventCount === 1 ? 'evento' : 'eventos'}
                     </button>
                  ) : (
                     <span className="subline-spacer" />
                  )}
                  <div className="subline-tags">
                     {breached && (
                        <span
                           className="breach-tag"
                           title="Chegada estimada (ETA) após o fim da janela de atendimento do cliente. Reordene a rota para tentar encaixar dentro do horário."
                        >
                           {HFIcon.clock} FORA DO HORÁRIO
                        </span>
                     )}
                     {stop.isLegacyDone && (
                        <span className="vol-tag">LEGADO</span>
                     )}
                     {isLocked && (
                        <span
                           className="lock-tag"
                           title="Item travado — tem evento ativo. Remova o evento na timeline para destravar."
                        >
                           {HFIcon.lock} TRAVADO
                        </span>
                     )}
                  </div>
               </div>
            )}
         </div>

         {/* Fluxo real (Ponto 4 §7): renderiza a timeline COMPLETA — todo
             event_kind em ordem cronológica, igual ao HISTÓRICO do drawer. OTW
             e sintéticos não são mais resumidos/escondidos. */}
         {showEvents && timeline &&
            timeline.map((ev, evIdx) =>
               ev.kind === 'otw' ? (
                  <div
                     // eslint-disable-next-line react/no-array-index-key
                     key={`${stop.id}-tl-${ev.eventId ?? evIdx}`}
                     className="hf-otw"
                  >
                     <div className="seq-spacer">{HFIcon.van}</div>
                     <div className="body">
                        <span className="t tabular">{ev.t}</span>
                        <span>—</span>
                        <span>{ev.label}</span>
                     </div>
                  </div>
               ) : (
                  <div
                     // eslint-disable-next-line react/no-array-index-key
                     key={`${stop.id}-tl-${ev.eventId ?? evIdx}`}
                     className={`hf-presence ${ev.kind}`}
                  >
                     <div className="dot-col">
                        <span className="d" />
                     </div>
                     <div className="body">
                        <span className="t tabular">{ev.t}</span>
                        <span>—</span>
                        <span className="l">{ev.label}</span>
                     </div>
                  </div>
               )
            )}

         {/* Fallback do mock (sem adapter): resumo presence + otw. */}
         {showEvents && !timeline &&
            stop.presence?.map((p, pIdx) => (
               <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${stop.id}-${p.kind}-${pIdx}`}
                  className={`hf-presence ${p.kind}`}
               >
                  <div className="dot-col">
                     <span className="d" />
                  </div>
                  <div className="body">
                     <span className="t tabular">{p.t}</span>
                     <span>—</span>
                     <span className="l">{p.label}</span>
                  </div>
               </div>
            ))}

         {showEvents && !timeline && stop.otw && !isLast && (
            <div className="hf-otw">
               <div className="seq-spacer">{HFIcon.van}</div>
               <div className="body">
                  <span className="t tabular">{stop.otw.t}</span>
                  <span>—</span>
                  <span>{stop.otw.label}</span>
               </div>
            </div>
         )}
      </>
   );
}

StopRow.propTypes = {
   stop: PropTypes.object.isRequired,
   isLast: PropTypes.bool.isRequired,
   isSelected: PropTypes.bool,
   onClick: PropTypes.func.isRequired,
   subclusterId: PropTypes.string.isRequired,
   index: PropTypes.number.isRequired,
   isDragging: PropTypes.bool,
   dropPosition: PropTypes.oneOf(['before', 'after', null]),
   canDrag: PropTypes.bool,
   routeColor: PropTypes.string,
   onDragStart: PropTypes.func.isRequired,
   onDragEnd: PropTypes.func.isRequired,
   onDragOver: PropTypes.func.isRequired,
   onDragLeave: PropTypes.func.isRequired,
   onDrop: PropTypes.func.isRequired,
};

// Linha de limites do subcluster (volumetria / tempo deslocamento / serviço).
// Só renderiza quando ao menos um limite estiver preenchido.
function RouteLimits({ limits }) {
   const parts = [];
   if (limits.volumetry != null) parts.push(`Vol ${limits.volumetry}`);
   if (limits.travelTime != null) parts.push(`${limits.travelTime}min desloc`);
   if (limits.serviceTime != null) {
      parts.push(`${limits.serviceTime}min serviço`);
   }
   if (parts.length === 0) return null;
   return (
      <div className="hf-route-limits tabular">
         {parts.map((p) => (
            <span key={p}>{p}</span>
         ))}
      </div>
   );
}

RouteLimits.propTypes = { limits: PropTypes.object.isRequired };

function SubclusterCard({
   sub,
   open,
   stops = null,
   onToggle,
   onStopClick,
   selectedStopId = null,
   drag = null,
   dropTarget = null,
   isStale = false,
   hidden = false,
   onToggleVisibility,
   canDrag = true,
   canManage = false,
   onDragStart,
   onDragEnd,
   onDragOver,
   onDragLeave,
   onDrop,
   onCardHover,
   deltaPill = null,
   onEdit,
   onDelete,
   onAssign,
   onAssignRoute,
   onRevertRoute,
}) {
   const { total, done, hasRun } = deriveProgress(stops);
   const isRun = hasRun ? 1 : 0;
   const { technician, vehicle } = sub;

   // Máquina de estados da rota (handoff 0042 §2). routeStatus pode vir undefined
   // no mock (subclusters cenográficos) — trata como RASCUNHO p/ não travar nada.
   const routeStatus = sub.routeStatus ?? 'RASCUNHO';
   // ATRIBUIDA: o backend (2026-06-24, handoff 0041 D5) passou a ACEITAR reorder
   // DENTRO da própria rota (membership idêntica). Continua recusando
   // adicionar/remover item e mover entre rotas (route_locked). Então:
   //   • linhas ARRASTÁVEIS mesmo em ATRIBUIDA (reorder interno liberado);
   //   • drop VINDO DE FORA (outra rota / dock) é NEGADO (add → route_locked).
   const routeLocked = routeStatus === 'ATRIBUIDA';
   const rowsCanDrag = canDrag;
   // Drag externo = item de outra rota ou do dock. Numa rota atribuída esse drop
   // insere/agrupa item novo → route_locked. O reorder interno (mesma rota) passa.
   const externalDrag =
      Boolean(drag) && (drag.source !== 'route' || drag.subclusterId !== sub.id);
   const blockDrop = routeLocked && externalDrag;

   // Balão do técnico segue o ESTADO da rota: ATRIBUIDA → cor da rota;
   // DESIGNADA → cinza. (Antes ficava preto na atribuída, ofuscando o nome.)
   const techBubbleColor =
      routeStatus === 'ATRIBUIDA' ? sub.color || '#9ca3af' : '#9ca3af';

   // Footer da rota (handoff 0030 — routeTotals/route_departure_time). km e
   // duração estimadas; janela planejada (início T0 + duração → fim). Sem
   // otimização os totais vêm null → "—".
   const km = sub.routeTotals?.distanceMeters;
   const durSec = sub.routeTotals?.durationSeconds;
   const startHHMM = toHHMM(sub.routeDepartureTime);
   let endHHMM = null;
   let routeWindow = '';
   if (startHHMM && durSec != null) {
      const [sh, sm] = startHHMM.split(':').map(Number);
      endHHMM = minutesToHHMM(sh * 60 + sm + Math.round(durSec / 60));
      routeWindow = `início às ${startHHMM} · fim às ${endHHMM}`;
   }

   // Linha de metadados do colapsado: "13,4 km · 1h20 · T0-13:45 · término 15:05".
   // Só entram os trechos disponíveis; sem otimização (totais null) cai em "—".
   // O T0 (horário de saída da base) entra em negrito para contextualizar a
   // âncora dos ETAs; o término é o fim planejado (T0 + duração). Sem duplicar o
   // T0 num intervalo início–fim (a janela era redundante com o próprio T0).
   const compactDur = compactRouteDuration(durSec);
   const metaParts = [];
   if (km != null) metaParts.push({ key: 'km', node: formatKm(km) });
   if (compactDur) metaParts.push({ key: 'dur', node: compactDur });
   if (startHHMM) {
      metaParts.push({
         key: 't0',
         node: <strong>{`T0-${startHHMM}`}</strong>,
      });
   }
   if (endHHMM) {
      metaParts.push({ key: 'end', node: `término ${endHHMM}` });
   }

   // Tipo de rota (handoff 0057) — 'Só ida' (one_way, sem retorno à base) vs
   // 'Ida e volta' (round_trip, default). Exibido apenas no footer do card
   // ABERTO (a linha compacta do colapsado trunca) — ver render do footer.
   const routeTypeLabel =
      sub.routeType === 'one_way' ? 'Só ida' : 'Ida e volta';

   // Menu de ações (kebab ⋯) — substitui a antiga statebar + ícones do corpo.
   const [menuOpen, setMenuOpen] = useState(false);
   const menuRef = useRef(null);
   useEffect(() => {
      if (!menuOpen) return undefined;
      const onDocClick = (e) => {
         if (menuRef.current && !menuRef.current.contains(e.target)) {
            setMenuOpen(false);
         }
      };
      const onKey = (e) => {
         if (e.key === 'Escape') setMenuOpen(false);
      };
      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('keydown', onKey);
      return () => {
         document.removeEventListener('mousedown', onDocClick);
         document.removeEventListener('keydown', onKey);
      };
   }, [menuOpen]);

   // Dispara a ação do menu e fecha o popover (sem alternar o card).
   const runMenu = (fn) => (e) => {
      e.stopPropagation();
      setMenuOpen(false);
      fn();
   };

   const stateMeta = ROUTE_STATE_META[routeStatus] || ROUTE_STATE_META.RASCUNHO;

   const handleStopsClick = (e) => e.stopPropagation();

   const isCardDropTarget =
      drag &&
      !blockDrop &&
      dropTarget?.subclusterId === sub.id &&
      dropTarget?.index === (stops?.length ?? 0);

   const handleCardDragOver = (e) => {
      if (!drag || blockDrop) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      // Card colapsado: agenda auto-expandir (hover-intent) p/ revelar as paradas
      // e deixar o operador soltar na posição certa do subcluster. O alvo no fim
      // da lista é só transitório até o card abrir (StopRow assume o posicionamento).
      if (!open) onCardHover(sub.id);
      onDragOver({ subclusterId: sub.id, index: stops?.length ?? 0 });
   };

   const handleCardDrop = (e) => {
      if (!drag || blockDrop) return;
      e.preventDefault();
      onDrop();
   };

   // Numa rota atribuída, as linhas não registram alvo de drop para item VINDO
   // DE FORA (impede inserir item de outra rota / do dock). O reorder interno
   // continua valendo (blockDrop só é true quando o drag é externo).
   const rowDragOver = blockDrop ? () => {} : onDragOver;
   const rowDrop = blockDrop ? () => {} : onDrop;

   return (
      <div
         className={`hf-tech hf-route ${open ? 'open' : ''} ${
            isCardDropTarget ? 'drop-target' : ''
         }`}
         onClick={onToggle}
         onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onToggle();
         }}
         role="button"
         tabIndex={0}
         onDragOver={handleCardDragOver}
         onDragLeave={onDragLeave}
         onDrop={handleCardDrop}
         style={{ position: 'relative' }}
      >
         {sub.color && (
            <span
               className="hf-route-stripe"
               style={{ background: sub.color }}
               aria-hidden="true"
            />
         )}
         {/* ── L1: nome + ícone do veículo + status + avatar do técnico + kebab.
            As ações migraram para o menu ⋯ (handoff Card de Rota D1). ── */}
         <div className="rc-head">
            <span className="rc-name" title={sub.label}>
               <span className="txt">{sub.label}</span>
               {vehicle && (
                  <span
                     className="rc-veh"
                     title={`Veículo: ${vehicle.identifier}`}
                     aria-label={`Veículo: ${vehicle.identifier}`}
                  >
                     {sub.vehicleClass === 'moto' ? HFIcon.moto : HFIcon.van}
                  </span>
               )}
            </span>
            <span className={`hf-pill ${pillClassFromStatus(sub.status)}`}>
               <span className="dot" />
               {sub.statusLbl}
            </span>
            {technician ? (
               <span
                  className="hf-av sm rc-av"
                  style={{ background: techBubbleColor }}
                  title={`Técnico: ${technician.name}`}
               >
                  {technician.init}
               </span>
            ) : (
               <span
                  className="hf-av sm rc-av ghost"
                  title="Sem técnico designado"
                  aria-label="Sem técnico designado"
               >
                  {HFIcon.user}
               </span>
            )}
            <button
               type="button"
               className={`rc-eye ${hidden ? 'off' : ''}`}
               title={hidden ? 'Mostrar rota no mapa' : 'Ocultar rota no mapa'}
               aria-label={
                  hidden ? 'Mostrar rota no mapa' : 'Ocultar rota no mapa'
               }
               aria-pressed={!hidden}
               onClick={(e) => {
                  e.stopPropagation();
                  onToggleVisibility(sub.id);
               }}
            >
               {hidden ? HFIcon.eyeOff : HFIcon.eye}
            </button>
            {canManage && (
            <div className="rc-more-wrap" ref={menuRef}>
               <button
                  type="button"
                  className="rc-more"
                  aria-label="Ações da rota"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  onClick={(e) => {
                     e.stopPropagation();
                     setMenuOpen((o) => !o);
                  }}
               >
                  {HFIcon.more}
               </button>
               {menuOpen && (
                  <div
                     className="rc-menu"
                     role="menu"
                     tabIndex={-1}
                     onClick={(e) => e.stopPropagation()}
                     onKeyDown={(e) => e.stopPropagation()}
                  >
                     <div className={`rc-menu-state ${stateMeta.cls}`}>
                        {stateMeta.label}
                     </div>
                     {canManage &&
                        (routeLocked ? (
                           <button
                              type="button"
                              role="menuitem"
                              className="rc-menu-item"
                              title="Destrava a rota para mover/reordenar itens."
                              onClick={runMenu(() => onRevertRoute(sub))}
                           >
                              {HFIcon.refresh} Reverter atribuição
                           </button>
                        ) : (
                           <button
                              type="button"
                              role="menuitem"
                              className="rc-menu-item"
                              disabled={routeStatus !== 'DESIGNADA'}
                              title={
                                 routeStatus === 'DESIGNADA'
                                    ? 'Propaga o técnico aos itens e trava a rota.'
                                    : 'Designe um técnico para a rota antes de atribuí-la.'
                              }
                              onClick={runMenu(() => onAssignRoute(sub))}
                           >
                              {HFIcon.check} Atribuir rota
                           </button>
                        ))}
                     {canManage && !routeLocked && (
                        <button
                           type="button"
                           role="menuitem"
                           className="rc-menu-item"
                           onClick={runMenu(() => onAssign(sub))}
                        >
                           {HFIcon.user}{' '}
                           {technician ? 'Trocar técnico' : 'Designar técnico'}
                        </button>
                     )}
                     {canManage && (
                        <button
                           type="button"
                           role="menuitem"
                           className="rc-menu-item"
                           onClick={runMenu(() => onEdit(sub))}
                        >
                           {HFIcon.pencil} Editar rota
                        </button>
                     )}
                     {canManage && (
                        <button
                           type="button"
                           role="menuitem"
                           className="rc-menu-item danger"
                           onClick={runMenu(() => onDelete(sub))}
                        >
                           {HFIcon.trash} Remover rota
                        </button>
                     )}
                  </div>
               )}
            </div>
            )}
         </div>

         {isStale && (
            <span
               className="hf-pill warn hf-route-stale"
               title="O mapa/ETA desta rota não pôde ser recalculado. Refaça um ajuste de ordem para tentar de novo."
            >
               {HFIcon.alert} Rota desatualizada
            </span>
         )}

         {/* ── L2: barra de progresso + fração concluídas/total ── */}
         <div className="rc-row2">
            <div className="progress">
               {Array.from({ length: total || 1 }).map((_, i) => {
                  const cls = progressSegClass(i, done, isRun);
                  // eslint-disable-next-line react/no-array-index-key
                  return <div key={i} className={`seg ${cls}`} />;
               })}
            </div>
            <span className="rc-frac tabular">
               {done}/{total}
            </span>
         </div>

         {/* ── L3: metadados (só no colapsado) — distância · duração · T0 · janela ── */}
         {!open && (
            <div className="rc-meta tabular">
               {metaParts.length
                  ? metaParts.map((part, i) => (
                       <React.Fragment key={part.key}>
                          {i > 0 && ' · '}
                          {part.node}
                       </React.Fragment>
                    ))
                  : '—'}
            </div>
         )}

         {/* Limites do subcluster — só no card aberto (compacta o colapsado). */}
         {open && <RouteLimits limits={sub.limits} />}

         {stops && (
            <div className="hf-tech-stops-wrap" aria-hidden={!open}>
               <div className="hf-tech-stops-inner">
                  <div
                     className="hf-stops"
                     onClick={handleStopsClick}
                     role="presentation"
                  >
                     {stops.map((s, idx) => {
                        const dragSameStop =
                           drag?.source === 'route' &&
                           drag?.subclusterId === sub.id &&
                           drag?.index === idx;
                        let dropPosition = null;
                        if (dropTarget?.subclusterId === sub.id) {
                           if (dropTarget.index === idx) {
                              dropPosition = 'before';
                           } else if (
                              dropTarget.index === idx + 1 &&
                              idx === stops.length - 1
                           ) {
                              dropPosition = 'after';
                           }
                        }
                        return (
                           <StopRow
                              key={s.id}
                              stop={s}
                              isLast={idx === stops.length - 1}
                              isSelected={selectedStopId === s.id}
                              onClick={() => onStopClick(s, sub)}
                              subclusterId={sub.id}
                              index={idx}
                              isDragging={dragSameStop}
                              dropPosition={dropPosition}
                              canDrag={rowsCanDrag}
                              routeColor={sub.color}
                              onDragStart={onDragStart}
                              onDragEnd={onDragEnd}
                              onDragOver={rowDragOver}
                              onDragLeave={onDragLeave}
                              onDrop={rowDrop}
                           />
                        );
                     })}
                     {stops.length === 0 && (
                        <div className="hf-route-empty">
                           Rota vazia — arraste pedidos do dock para cá.
                        </div>
                     )}
                  </div>
               </div>
            </div>
         )}

         {/* Footer "planejado" — só no card aberto (handoff Card de Rota D1).
            Distância + duração planejadas lado a lado, com divisória. Label
            (10px) em cima, métrica (16px) embaixo. Sem otimização → "—". */}
         {open && (
            <div className="hf-route-footer tabular">
               <div className="metric">
                  <span className="label">Distância planejada</span>
                  <span className="value">{formatKm(km)}</span>
                  {/* Tipo de rota (handoff 0057) — resumido, sem label, sob o
                     numeral de km, no mesmo tom discreto da janela ao lado da
                     duração. */}
                  <span className="window">{routeTypeLabel}</span>
               </div>
               <span className="sep" aria-hidden="true" />
               <div className="metric">
                  <span className="label">Duração planejada</span>
                  <span className="value">{formatRouteDuration(durSec)}</span>
                  {routeWindow && <span className="window">{routeWindow}</span>}
               </div>
            </div>
         )}

         {deltaPill && deltaPill.subclusterId === sub.id && (
            <div className="hf-drop-pill">{deltaPill.text}</div>
         )}
      </div>
   );
}

SubclusterCard.propTypes = {
   sub: PropTypes.object.isRequired,
   open: PropTypes.bool.isRequired,
   stops: PropTypes.array,
   onToggle: PropTypes.func.isRequired,
   onStopClick: PropTypes.func.isRequired,
   selectedStopId: PropTypes.string,
   drag: PropTypes.object,
   dropTarget: PropTypes.object,
   isStale: PropTypes.bool,
   hidden: PropTypes.bool,
   onToggleVisibility: PropTypes.func.isRequired,
   canDrag: PropTypes.bool,
   canManage: PropTypes.bool,
   onDragStart: PropTypes.func.isRequired,
   onDragEnd: PropTypes.func.isRequired,
   onDragOver: PropTypes.func.isRequired,
   onDragLeave: PropTypes.func.isRequired,
   onDrop: PropTypes.func.isRequired,
   onCardHover: PropTypes.func.isRequired,
   deltaPill: PropTypes.object,
   onEdit: PropTypes.func.isRequired,
   onDelete: PropTypes.func.isRequired,
   onAssign: PropTypes.func.isRequired,
   onAssignRoute: PropTypes.func.isRequired,
   onRevertRoute: PropTypes.func.isRequired,
};

export default function HFRoutePanel({
   subclusters,
   stopsBySubcluster,
   openSubcluster = null,
   onOpenSubcluster,
   tabs,
   routesCount = 0,
   breachCount = 0,
   onStopClick,
   selectedStopId = null,
   drag = null,
   dropTarget = null,
   staleIds = null,
   hiddenRouteIds = null,
   onToggleVisibility,
   onToggleAllVisibility,
   allRoutesHidden = false,
   canDrag = true,
   canManage = false,
   emptyHint = null,
   onDragStart,
   onDragEnd,
   onDragOver,
   onDragLeave,
   onDrop,
   deltaPill = null,
   onCreate,
   onEdit,
   onDelete,
   onAssign,
   onAssignRoute,
   onRevertRoute,
}) {
   const [isCollapsed, setIsCollapsed] = useState(false);

   // ─── Busca de pedido/cliente roteirizado (dropdown de sugestões) ──────────
   // Filtra CLIENT-SIDE os stops já carregados (snapshot) por nº do pedido OU
   // nome do cliente. Não chama API — por isso o dropdown filtra enquanto digita
   // (seletor de entidade, isento da regra de busca-explícita das listagens).
   // Selecionar uma sugestão = mesmo gesto do clique no pino roteirizado do mapa
   // (handlePinClick): abre a ficha (onStopClick) + expande a rota
   // (onOpenSubcluster); o StopRow rola sozinho até a parada (scrollIntoView).
   const [search, setSearch] = useState('');
   const [searchFocused, setSearchFocused] = useState(false);
   const searchInputRef = useRef(null);
   const SEARCH_RESULT_CAP = 5;

   const searchResults = useMemo(() => {
      const q = search.trim().toLowerCase();
      if (!q) return [];
      const out = [];
      for (let i = 0; i < subclusters.length; i += 1) {
         const sub = subclusters[i];
         const stops = stopsBySubcluster[sub.id] || [];
         for (let j = 0; j < stops.length; j += 1) {
            const stop = stops[j];
            const order = String(stop.id || '').toLowerCase();
            const name = String(stop.nm || '').toLowerCase();
            if (order.includes(q) || name.includes(q)) {
               out.push({ stop, sub });
               if (out.length >= SEARCH_RESULT_CAP) return out;
            }
         }
      }
      return out;
   }, [search, subclusters, stopsBySubcluster]);

   // Há item roteirizado pra buscar? (bloco não-vazio). Gateia o botão de busca:
   // azul = ação disponível; cinza/disabled = nada a pesquisar.
   const hasRoutedStops = useMemo(
      () =>
         subclusters.some(
            (s) => (stopsBySubcluster[s.id] || []).length > 0
         ),
      [subclusters, stopsBySubcluster]
   );

   const handleSearchSelect = (stop, sub) => {
      onStopClick(stop, sub);
      onOpenSubcluster(sub.id);
      setSearch('');
      setSearchFocused(false);
   };

   // ─── Quebras de R.H (handoff 0049): a badge vira ação ─────────────────────
   // O contador (breachCount) é autoritativo do backend (summary.time_window_
   // breaches). A badge abre um dropdown — mesma forma do dropdown de busca —
   // listando os itens roteirizados com ETA após o fim da janela, ATRAVÉS de
   // todas as rotas (impossível "filtrar a lista": vivem em subclusters
   // separados). Selecionar = mesmo gesto da busca (abre ficha + expande rota +
   // scrollIntoView). Client-side sobre o snapshot já carregado; não chama API.
   const [breachOpen, setBreachOpen] = useState(false);
   const breachRef = useRef(null);

   const breachedStops = useMemo(() => {
      const out = [];
      for (let i = 0; i < subclusters.length; i += 1) {
         const sub = subclusters[i];
         const stops = stopsBySubcluster[sub.id] || [];
         for (let j = 0; j < stops.length; j += 1) {
            if (stops[j].timeWindowBreach) out.push({ stop: stops[j], sub });
         }
      }
      return out;
   }, [subclusters, stopsBySubcluster]);

   const handleBreachSelect = (stop, sub) => {
      onStopClick(stop, sub);
      onOpenSubcluster(sub.id);
      setBreachOpen(false);
   };

   // Fecha no clique-fora / Esc (espelha o menu ⋯ do card de rota).
   useEffect(() => {
      if (!breachOpen) return undefined;
      const onDocClick = (e) => {
         if (breachRef.current && !breachRef.current.contains(e.target)) {
            setBreachOpen(false);
         }
      };
      const onKey = (e) => {
         if (e.key === 'Escape') setBreachOpen(false);
      };
      document.addEventListener('mousedown', onDocClick);
      document.addEventListener('keydown', onKey);
      return () => {
         document.removeEventListener('mousedown', onDocClick);
         document.removeEventListener('keydown', onKey);
      };
   }, [breachOpen]);

   // breachCount → 0 (reorder reencaixou tudo) desmonta a badge; zera o estado
   // pra o popover não reabrir sozinho quando uma nova quebra surgir depois.
   useEffect(() => {
      if (breachCount === 0) setBreachOpen(false);
   }, [breachCount]);

   // ─── Auto-scroll controlado durante o drag ───────────────────────────────
   // O auto-scroll NATIVO do HTML5 DnD é rápido/abrupto demais. Com o body em
   // `overflow:hidden` durante o drag (classe .dragging) o navegador deixa de
   // rolar sozinho; nós dirigimos o scrollTop via rAF, devagar e proporcional à
   // proximidade da borda. O listener é em CAPTURE p/ rodar mesmo quando o
   // StopRow dá stopPropagation no dragover (bubble).
   const bodyRef = useRef(null);
   const scrollRaf = useRef(null);
   const scrollSpeed = useRef(0);

   const stopAutoScroll = useCallback(() => {
      if (scrollRaf.current) {
         cancelAnimationFrame(scrollRaf.current);
         scrollRaf.current = null;
      }
      scrollSpeed.current = 0;
   }, []);

   const stepAutoScroll = useCallback(() => {
      const el = bodyRef.current;
      if (!el || !scrollSpeed.current) {
         scrollRaf.current = null;
         return;
      }
      el.scrollTop += scrollSpeed.current;
      scrollRaf.current = requestAnimationFrame(stepAutoScroll);
   }, []);

   // ─── Hover-intent: expandir o subcluster-alvo durante o drag ─────────────
   // Cross-subcluster: ao pairar (≈160ms) sobre um card colapsado, ele abre p/
   // o operador soltar na ordem certa. Atraso evita expandir/recolher em cascata
   // ao arrastar passando por vários cards até o alvo.
   const hoverExpand = useRef({ id: null, timer: null });
   const clearHoverExpand = useCallback(() => {
      if (hoverExpand.current.timer) clearTimeout(hoverExpand.current.timer);
      hoverExpand.current = { id: null, timer: null };
   }, []);

   const handleCardHover = useCallback(
      (subId) => {
         if (subId === openSubcluster) {
            clearHoverExpand();
            return;
         }
         if (hoverExpand.current.id === subId) return; // já agendado
         clearHoverExpand();
         hoverExpand.current.id = subId;
         hoverExpand.current.timer = setTimeout(() => {
            onOpenSubcluster(subId);
            hoverExpand.current = { id: null, timer: null };
         }, 160);
      },
      [openSubcluster, onOpenSubcluster, clearHoverExpand]
   );

   useEffect(() => {
      const el = bodyRef.current;
      if (!el) return undefined;

      const EDGE = 56; // zona de borda (px)
      const MAX = 9; // velocidade máx (px/frame)
      const onDragOverCapture = (e) => {
         const rect = el.getBoundingClientRect();
         const y = e.clientY;
         let speed = 0;
         if (y < rect.top + EDGE) {
            const t = Math.min(1, (rect.top + EDGE - y) / EDGE);
            speed = -Math.max(1, Math.ceil(t * MAX));
         } else if (y > rect.bottom - EDGE) {
            const t = Math.min(1, (y - (rect.bottom - EDGE)) / EDGE);
            speed = Math.max(1, Math.ceil(t * MAX));
         }
         scrollSpeed.current = speed;
         if (speed && !scrollRaf.current) {
            scrollRaf.current = requestAnimationFrame(stepAutoScroll);
         } else if (!speed) {
            stopAutoScroll();
         }
      };
      const onDragDone = () => {
         stopAutoScroll();
         clearHoverExpand();
      };

      el.addEventListener('dragover', onDragOverCapture, true);
      document.addEventListener('dragend', onDragDone, true);
      el.addEventListener('drop', onDragDone, true);
      return () => {
         el.removeEventListener('dragover', onDragOverCapture, true);
         document.removeEventListener('dragend', onDragDone, true);
         el.removeEventListener('drop', onDragDone, true);
         stopAutoScroll();
         clearHoverExpand();
      };
   }, [stepAutoScroll, stopAutoScroll, clearHoverExpand]);

   return (
      <div className={`hf-panel-left ${isCollapsed ? 'collapsed' : ''}`}>
         <div className="hf-panel-tabs">
            {tabs.map((t) => (
               <button
                  type="button"
                  key={t.key}
                  className={`hf-panel-tab ${t.active ? 'active' : ''}`}
               >
                  {t.label} <span className="ct">{t.count}</span>
               </button>
            ))}
            {subclusters.length > 0 && (
               <button
                  type="button"
                  className={`panel-vis-btn ${allRoutesHidden ? 'off' : ''}`}
                  onClick={onToggleAllVisibility}
                  title={
                     allRoutesHidden
                        ? 'Mostrar todas as rotas no mapa'
                        : 'Ocultar todas as rotas no mapa'
                  }
                  aria-label={
                     allRoutesHidden
                        ? 'Mostrar todas as rotas no mapa'
                        : 'Ocultar todas as rotas no mapa'
                  }
               >
                  {allRoutesHidden ? HFIcon.eyeOff : HFIcon.eye}
               </button>
            )}
            <button
               type="button"
               className="panel-collapse-btn"
               onClick={() => setIsCollapsed((prev) => !prev)}
               title={isCollapsed ? 'Expandir' : 'Recolher'}
               aria-label={isCollapsed ? 'Expandir painel' : 'Recolher painel'}
            >
               {isCollapsed ? HFIcon.chevU : HFIcon.chevD}
            </button>
         </div>
         <div className="hf-panel-search-wrap">
            <div className="hf-panel-search">
               <input
                  ref={searchInputRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                  placeholder="Buscar por nº pedido ou nome do cliente..."
                  aria-label="Buscar por nº pedido ou nome do cliente"
               />
               {/* Botão à direita (espelha o dock): lupa quando vazio (foca o
                  campo), X para limpar quando há texto. mousedown preventDefault
                  evita o blur do input fechar o dropdown antes do clique. */}
               <button
                  type="button"
                  className="hf-panel-search-btn"
                  aria-label={search ? 'Limpar busca' : 'Buscar'}
                  disabled={!hasRoutedStops && !search}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                     if (search) setSearch('');
                     else searchInputRef.current?.focus();
                  }}
               >
                  {search ? HFIcon.x : HFIcon.search}
               </button>
            </div>
            {searchFocused && search.trim() && (
               <div className="hf-panel-search-results">
                  {searchResults.length === 0 ? (
                     <div className="hf-search-empty">
                        Nenhum pedido ou cliente roteirizado encontrado.
                     </div>
                  ) : (
                     searchResults.map(({ stop, sub }) => (
                        <button
                           type="button"
                           key={`${sub.id}-${stop.id}`}
                           className="hf-search-sug"
                           // preventDefault no mousedown: o clique registra antes
                           // do onBlur do input fechar o dropdown.
                           onMouseDown={(e) => e.preventDefault()}
                           onClick={() => handleSearchSelect(stop, sub)}
                        >
                           <span className="sug-top">
                              <span className="sug-order">PED-{stop.id}</span>
                              <span
                                 className="sug-route"
                                 style={{ '--rc': sub.color || '#888' }}
                              >
                                 <span className="dot" />
                                 {sub.label}
                                 {stop.seq ? ` · #${stop.seq}` : ''}
                              </span>
                           </span>
                           <span className="sug-name">{stop.nm}</span>
                        </button>
                     ))
                  )}
               </div>
            )}
         </div>
         {/* "Rotas N" reposicionado pra junto da ação de criar rota (era uma tab
            no topo). N = nº de rotas/subclusters do agendamento. */}
         <div className="hf-routes-label">
            Rotas <span className="ct">{routesCount}</span>
            {breachCount > 0 && (
               <span className="hf-breach-wrap" ref={breachRef}>
                  <button
                     type="button"
                     className={`hf-breach-badge ${breachOpen ? 'open' : ''}`}
                     aria-haspopup="menu"
                     aria-expanded={breachOpen}
                     title="Restrição de horário marcado — itens roteirizados cuja chegada estimada (ETA) passou do fim da janela do cliente. Clique para ver quais e ir até eles."
                     onClick={() => setBreachOpen((o) => !o)}
                  >
                     {HFIcon.clock} Quebras de R.H{' '}
                     <span className="ct">{breachCount}</span>
                  </button>
                  {breachOpen && (
                     <div className="hf-breach-results" role="menu">
                        {breachedStops.length === 0 ? (
                           <div className="hf-search-empty">
                              Não foi possível localizar os itens no snapshot
                              atual — atualize o quadro.
                           </div>
                        ) : (
                           breachedStops.map(({ stop, sub }) => (
                              <button
                                 type="button"
                                 role="menuitem"
                                 key={`${sub.id}-${stop.id}`}
                                 className="hf-search-sug"
                                 onClick={() => handleBreachSelect(stop, sub)}
                              >
                                 <span className="sug-top">
                                    <span className="sug-order">
                                       PED-{stop.id}
                                    </span>
                                    <span
                                       className="sug-route"
                                       style={{ '--rc': sub.color || '#888' }}
                                    >
                                       <span className="dot" />
                                       {sub.label}
                                       {stop.seq ? ` · #${stop.seq}` : ''}
                                    </span>
                                 </span>
                                 <span className="sug-name">{stop.nm}</span>
                                 <span className="sug-breach">
                                    {HFIcon.clock} ETA {stop.eta || '—'}
                                    {stop.win ? ` · janela ${stop.win}` : ''}
                                 </span>
                              </button>
                           ))
                        )}
                     </div>
                  )}
               </span>
            )}
         </div>
         {canManage && (
            <button type="button" className="hf-new-route" onClick={onCreate}>
               {HFIcon.plus} Nova rota
            </button>
         )}
         <div
            className={`hf-panel-body scroll ${drag ? 'dragging' : ''}`}
            ref={bodyRef}
         >
            {subclusters.map((sub) => (
               <SubclusterCard
                  key={sub.id}
                  sub={sub}
                  open={openSubcluster === sub.id}
                  stops={stopsBySubcluster[sub.id]}
                  onToggle={() =>
                     onOpenSubcluster(openSubcluster === sub.id ? null : sub.id)
                  }
                  onStopClick={onStopClick}
                  selectedStopId={selectedStopId}
                  drag={drag}
                  dropTarget={dropTarget}
                  isStale={Boolean(staleIds && staleIds.has(sub.id))}
                  hidden={Boolean(hiddenRouteIds && hiddenRouteIds.has(sub.id))}
                  onToggleVisibility={onToggleVisibility}
                  canDrag={canDrag}
                  canManage={canManage}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  onCardHover={handleCardHover}
                  deltaPill={deltaPill}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onAssign={onAssign}
                  onAssignRoute={onAssignRoute}
                  onRevertRoute={onRevertRoute}
               />
            ))}
            {subclusters.length === 0 && emptyHint && (
               <div style={{ padding: 16, fontSize: 13, color: '#8B8B82' }}>
                  {emptyHint}
               </div>
            )}
         </div>
      </div>
   );
}

HFRoutePanel.propTypes = {
   subclusters: PropTypes.array.isRequired,
   stopsBySubcluster: PropTypes.object.isRequired,
   openSubcluster: PropTypes.string,
   onOpenSubcluster: PropTypes.func.isRequired,
   tabs: PropTypes.array.isRequired,
   routesCount: PropTypes.number,
   breachCount: PropTypes.number,
   onStopClick: PropTypes.func.isRequired,
   selectedStopId: PropTypes.string,
   drag: PropTypes.object,
   dropTarget: PropTypes.object,
   // eslint-disable-next-line react/forbid-prop-types
   staleIds: PropTypes.object,
   // eslint-disable-next-line react/forbid-prop-types
   hiddenRouteIds: PropTypes.object,
   onToggleVisibility: PropTypes.func.isRequired,
   onToggleAllVisibility: PropTypes.func.isRequired,
   allRoutesHidden: PropTypes.bool,
   canDrag: PropTypes.bool,
   canManage: PropTypes.bool,
   emptyHint: PropTypes.string,
   onDragStart: PropTypes.func.isRequired,
   onDragEnd: PropTypes.func.isRequired,
   onDragOver: PropTypes.func.isRequired,
   onDragLeave: PropTypes.func.isRequired,
   onDrop: PropTypes.func.isRequired,
   deltaPill: PropTypes.object,
   onCreate: PropTypes.func.isRequired,
   onEdit: PropTypes.func.isRequired,
   onDelete: PropTypes.func.isRequired,
   onAssign: PropTypes.func.isRequired,
   onAssignRoute: PropTypes.func.isRequired,
   onRevertRoute: PropTypes.func.isRequired,
};
